import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import { SUPPORTED_LANGUAGES, BASE_LANGUAGE_CODES } from "src/i18n";
import { useEnabledLanguages } from "src/hooks/useEnabledLanguages";

type Language = (typeof SUPPORTED_LANGUAGES)[number];

// Default when no provider is mounted: the always-on base languages. This is a
// safe fail-closed fallback (never offers an unreleased language) and keeps
// consumers like LanguageSelector usable in isolation (e.g. unit tests that
// render a parent without the provider).
const BASE_LANGUAGES: Language[] = SUPPORTED_LANGUAGES.filter((l) => BASE_LANGUAGE_CODES.includes(l.code));

// Holds the released-language list so the per-language feature flags are
// evaluated ONCE (in the provider) rather than once per consumer.
const EnabledLanguagesContext = createContext<Language[]>(BASE_LANGUAGES);

/**
 * Evaluates the enabled-language set once and shares it with all consumers
 * (LanguageGate, LanguageSelector) via context. The hook already returns a
 * reference-stable array (memoized on the flag values), so consumers can use it
 * directly as a dependency without extra re-renders.
 */
export const EnabledLanguagesProvider = ({ children }: { children: ReactNode }): ReactNode => {
  const enabled = useEnabledLanguages();
  return <EnabledLanguagesContext.Provider value={enabled}>{children}</EnabledLanguagesContext.Provider>;
};

/** The released languages (base + flag-enabled), from context. */
export function useEnabledLanguagesContext(): Language[] {
  return useContext(EnabledLanguagesContext);
}

export default EnabledLanguagesProvider;
